import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Layout from "@/components/Layout";
import { api } from "@/lib/api";
import { fmt, fmtDate } from "@/lib/format";
import ColumnFilter from "@/components/ColumnFilter";
import { useColFilter } from "@/lib/tableFilter";
import { Phone, Scale, Users } from "lucide-react";

const DURUM_LABEL = { gecikmis: "Gecikmiş", yaklasan: "Yaklaşan (30 gün)", ileri: "İleri Vadeli", belirsiz: "Tarihsiz" };
const DURUM_TONE = { gecikmis: "#A63A3A", yaklasan: "#B8933F", ileri: "#8C8377", belirsiz: "#A79A86" };

function DurumBadge({ durum, gun }) {
  const tone = DURUM_TONE[durum] || "#8C8377";
  let txt = DURUM_LABEL[durum] || durum;
  if (durum === "gecikmis" && gun < 90000) txt = `${Math.abs(gun)} gün gecikmiş`;
  else if (durum === "yaklasan") txt = gun === 0 ? "Bugün" : `${gun} gün kaldı`;
  return <span className="px-2 py-0.5 rounded-sm text-xs font-semibold" style={{ backgroundColor: `${tone}1a`, color: tone }}>{txt}</span>;
}

function PromiseTable({ cols, rows, onRowClick, testId }) {
  const { colSel, setColSel, sort, setSort, view, distinct } = useColFilter(cols, rows);
  const totTutar = view.reduce((s, r) => s + (r.taahhut_tutari || 0), 0);
  return (
    <div className="bg-white border border-[#E7DECC] rounded-sm overflow-hidden" data-testid={testId}>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-[#E7DECC] bg-[#F3ECDD]">
              {cols.map((c) => (
                <th key={c.key} className={`px-3 py-2.5 kicker ${c.type === "money" ? "text-right" : "text-left"}`}>
                  <div className={`flex items-center gap-1 ${c.type === "money" ? "justify-end" : "justify-between"}`}>
                    <span>{c.label}{sort.key === c.key ? (sort.dir === "asc" ? " ↑" : " ↓") : ""}</span>
                    <ColumnFilter col={c} values={distinct(c)} selected={colSel[c.key] ?? null} onChange={(v) => setColSel((p) => ({ ...p, [c.key]: v }))} sortDir={sort.key === c.key ? sort.dir : null} onSort={(dir) => setSort({ key: c.key, dir })} onClearSort={() => setSort((s) => (s.key === c.key ? { key: null, dir: null } : s))} />
                  </div>
                </th>
              ))}
              <th className="px-3 py-2.5 kicker text-left">Durum</th>
            </tr>
          </thead>
          <tbody>
            {view.length === 0 ? (
              <tr><td colSpan={cols.length + 1} className="px-3 py-10 text-center text-[#8C8377]" data-testid={`${testId}-empty`}>Kayıtlı ödeme sözü yok.</td></tr>
            ) : view.map((r, i) => (
              <tr key={i} className="border-b border-[#E7DECC] hover:bg-[#EFE7D6] cursor-pointer transition-colors duration-150" onClick={() => onRowClick(r)} data-testid={`${testId}-row-${i}`}>
                {cols.map((c) => (
                  <td key={c.key} className={`px-3 py-2 ${c.type === "money" ? "text-right tabular-nums" : ""} ${c.key === "musteri" ? "font-medium" : c.type === "date" ? "tabular-nums" : "text-[#8C8377]"}`}>
                    {c.type === "money" ? (r[c.key] ? fmt(r[c.key]) : "-") : c.type === "date" ? (r[c.key] ? fmtDate(r[c.key]) : "-") : (r[c.key] || "-")}
                  </td>
                ))}
                <td className="px-3 py-2"><DurumBadge durum={r.durum} gun={r.gun} /></td>
              </tr>
            ))}
          </tbody>
          {view.length > 0 && (
            <tfoot>
              <tr className="border-t-2 border-[#E7DECC] font-bold bg-[#F3ECDD]">
                <td className="px-3 py-2.5" colSpan={cols.length}>TOPLAM ({view.length}) — Taahhüt: {fmt(totTutar)}</td>
                <td></td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </div>
  );
}

const ICRA_COLS = [
  { key: "musteri", label: "Borçlu", type: "text" },
  { key: "dosya_no", label: "Dosya No", type: "text" },
  { key: "taahhut_tarihi", label: "Taahhüt Tarihi", type: "date" },
  { key: "taahhut_tutari", label: "Taahhüt Tutarı", type: "money" },
  { key: "temsilci", label: "Temsilci", type: "text" },
];
const CARI_COLS = [
  { key: "musteri", label: "Müşteri", type: "text" },
  { key: "taahhut_tarihi", label: "Taahhüt Tarihi", type: "date" },
  { key: "taahhut_tutari", label: "Taahhüt Tutarı", type: "money" },
  { key: "temsilci", label: "Temsilci", type: "text" },
];

export default function OdemeSozleri() {
  const [data, setData] = useState({ icra: [], cari: [] });
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("icra");
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/odeme-sozleri/tumu").then((r) => setData(r.data || { icra: [], cari: [] })).finally(() => setLoading(false));
  }, []);

  const rows = tab === "icra" ? data.icra : data.cari;
  const gecikmis = rows.filter((r) => r.durum === "gecikmis").length;
  const yaklasan = rows.filter((r) => r.durum === "yaklasan").length;
  const toplamTutar = rows.reduce((s, r) => s + (r.taahhut_tutari || 0), 0);

  return (
    <Layout>
      <div className="kicker mb-1">TAKİP</div>
      <h1 className="font-heading text-3xl font-bold tracking-tight mb-1">Ödeme Sözleri</h1>
      <p className="text-sm text-[#8C8377] mb-4">Tüm İcra ve Cari ödeme sözü taahhütleri — gecikmiş ve yaklaşan. Satıra tıklayarak dosyayı açın.</p>

      <div className="flex items-center gap-2 mb-4" data-testid="odeme-sozleri-tabs">
        <button onClick={() => setTab("icra")} data-testid="odeme-sozleri-tab-icra" className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-sm text-sm font-medium transition-colors duration-150 ${tab === "icra" ? "bg-[#B8933F] text-white" : "bg-white border border-[#E7DECC] text-[#8C8377] hover:bg-[#EFE7D6]"}`}>
          <Scale className="w-4 h-4" /> İcra ({data.icra.length})
        </button>
        <button onClick={() => setTab("cari")} data-testid="odeme-sozleri-tab-cari" className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-sm text-sm font-medium transition-colors duration-150 ${tab === "cari" ? "bg-[#B8933F] text-white" : "bg-white border border-[#E7DECC] text-[#8C8377] hover:bg-[#EFE7D6]"}`}>
          <Users className="w-4 h-4" /> Cari ({data.cari.length})
        </button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        <div className="bg-white border border-[#E7DECC] rounded-sm p-4" style={{ borderTop: "3px solid #A63A3A" }} data-testid="odeme-sozleri-gecikmis">
          <div className="kicker mb-1.5">Gecikmiş Taahhüt</div>
          <div className="font-heading font-bold text-lg tabular-nums text-[#A63A3A]">{gecikmis}</div>
        </div>
        <div className="bg-white border border-[#E7DECC] rounded-sm p-4" style={{ borderTop: "3px solid #B8933F" }} data-testid="odeme-sozleri-yaklasan">
          <div className="kicker mb-1.5">Yaklaşan (30 gün)</div>
          <div className="font-heading font-bold text-lg tabular-nums text-[#B8933F]">{yaklasan}</div>
        </div>
        <div className="bg-white border border-[#E7DECC] rounded-sm p-4" style={{ borderTop: "3px solid #B8933F" }} data-testid="odeme-sozleri-toplam">
          <div className="kicker mb-1.5">Toplam Taahhüt Tutarı</div>
          <div className="font-heading font-bold text-lg tabular-nums text-[#B8933F]">{fmt(toplamTutar)}</div>
        </div>
      </div>

      {loading ? (
        <div className="text-[#8C8377] text-sm">Yükleniyor...</div>
      ) : tab === "icra" ? (
        <PromiseTable cols={ICRA_COLS} rows={data.icra} onRowClick={(r) => navigate(`/icra/${r.rid}`)} testId="odeme-sozleri-icra-table" />
      ) : (
        <PromiseTable cols={CARI_COLS} rows={data.cari} onRowClick={(r) => navigate(`/cari-musteri/${encodeURIComponent(r.musteri)}`)} testId="odeme-sozleri-cari-table" />
      )}
    </Layout>
  );
}
